import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useLang, LANGUAGES } from "../../contexts/LanguageContext";
import { api } from "../../lib/api";
import { useSeoContext } from "./seo/PageSeo";

/**
 * Editable, language-aware SEO tags for the public site.
 *
 * Writes:
 *   - document.title
 *   - <meta name="description">
 *   - <meta property="og:*"> + Twitter card
 *   - <link rel="canonical"> for the current URL
 *   - <link rel="alternate" hreflang="es|fr|en|ar"> pointing to the SAME path
 *     under every supported language + a `x-default` alias.
 *
 * Every SPA route change re-runs the effect so the title/meta tags stay in
 * sync when a visitor navigates client-side or toggles the language.
 *
 * NEVER included on /admin pages.
 */
export default function Seo() {
  const { lang } = useLang();
  const { pathname, search } = useLocation();
  const { pageMeta } = useSeoContext();
  const [settings, setSettings] = useState(null);

  // Cache the settings response so the effect below can re-merge with pageMeta
  // without a fresh network round-trip on every route/language toggle.
  useEffect(() => {
    let cancelled = false;
    api.get("/settings").then((r) => { if (!cancelled) setSettings(r.data || {}); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!settings) return;
    const managed = [];
    const upsertMeta = (attr, key, content) => {
      if (!content) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        el.setAttribute("data-cecit-seo", "1");
        document.head.appendChild(el);
        managed.push(el);
      }
      el.setAttribute("content", content);
    };
    const upsertLink = (rel, hreflang, href) => {
      if (!href) return;
      const selector = hreflang
        ? `link[rel="${rel}"][hreflang="${hreflang}"]`
        : `link[rel="${rel}"]:not([hreflang])`;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        if (hreflang) el.setAttribute("hreflang", hreflang);
        el.setAttribute("data-cecit-seo", "1");
        document.head.appendChild(el);
        managed.push(el);
      }
      el.setAttribute("href", href);
    };

    const s = settings;
    const pick = (key) => s[`${key}_${lang}`] || s[`${key}_es`] || "";

    // Merge globals with per-page overrides (from usePageSeo). Page fields
    // that are set win; empty/null fields fall back to the global defaults.
    const defaultTitle = pick("seo_title") || "CECIT";
    const defaultDescription = pick("seo_description") || "";
    const title = (pageMeta && pageMeta.title && pageMeta.title.trim()) || defaultTitle;
    const description = (pageMeta && pageMeta.description && pageMeta.description.trim()) || defaultDescription;
    const ogImage = (pageMeta && pageMeta.image) || s.seo_default_og_image || "";

    const origin = (s.seo_canonical_base && s.seo_canonical_base.trim()) || (typeof window !== "undefined" ? window.location.origin : "");
    const canonicalUrl = origin ? `${origin.replace(/\/$/, "")}${pathname}${search || ""}` : "";

    document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", pageMeta?.type || "website");
    upsertMeta("property", "og:locale", lang === "ar" ? "ar_MA" : lang === "fr" ? "fr_FR" : lang === "en" ? "en_US" : "es_ES");
    if (canonicalUrl) upsertMeta("property", "og:url", canonicalUrl);
    if (ogImage) upsertMeta("property", "og:image", ogImage);
    upsertMeta("name", "twitter:card", ogImage ? "summary_large_image" : "summary");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    if (ogImage) upsertMeta("name", "twitter:image", ogImage);

    if (canonicalUrl) upsertLink("canonical", null, canonicalUrl);
    if (origin) {
      LANGUAGES.forEach((l) => {
        upsertLink("alternate", l.code, `${origin.replace(/\/$/, "")}${pathname}${search || ""}`);
      });
      upsertLink("alternate", "x-default", `${origin.replace(/\/$/, "")}${pathname}${search || ""}`);
    }
  }, [settings, lang, pathname, search, pageMeta]);

  return null;
}
